/* eslint-disable react/jsx-no-bind */
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

import { Box, Grid, ThemeProvider, Stack, useTheme } from "@mui/material";
import { DashboardRoutes, FormStatus, OrganizationRoutes } from "@simplyk/common";
import { enqueueSnackbar } from "notistack";

import { AutoHideDuration } from "../../../constants/Notistack";
import { FrontendFormContext } from "../../../contexts/FrontendFormContext";
import { GetCurrentUserFrontendObject } from "../../../contexts/types";
import { getEditFormLink } from "../../../features/Campaigns/helper/getEditFormLink";
import { useBackToFormTable } from "../../../features/LiveFormEditor/Navigation/useBackToFormTabs";
import { getDashboardLink } from "../../../helpers/link-to-dashboard";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { useTranslate } from "../../../hooks/useTranslate";
import { zeffyTheme } from "../../../theme/theme";
import { ButtonCopy } from "../../ButtonCopy/ButtonCopy";
import { NotificationBannerHeightContext } from "../NotificationBannerHeightContext";
import { useSyncNotificationBannerHeight } from "../useSyncNotificationBannerHeight";

import { useShouldDisplayFormOwnerBanner } from "./useShouldDisplayFormOwnerBanner";

import { ConnectedShareFormModal } from "@/components/ShareFormModal";
import { DashboardButton } from "@/dashboard-design-system/Button/DashboardButton";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardIconButton } from "@/dashboard-design-system/IconButton";
import { DashboardLink } from "@/dashboard-design-system/Link";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { parseAsBoolean, useQueryState } from "@/hooks/useQueryState";
import { ChevronUp, Info, Reveal as Eye } from "@/icons/outlined";

const EXPAND_BUTTON_WIDTH = 48;

export const FormOwnerBanner = ({
  loggedOrganization,
}: {
  loggedOrganization: GetCurrentUserFrontendObject["currentOrganization"] | null | undefined;
}) => {
  const { isSmallScreen } = useMediaQuery();
  const { t } = useTranslate();
  const theme = useTheme();
  const { formData, formType } = useContext(FrontendFormContext);

  const [isShareModalOpen, setIsShareModalOpen] = useQueryState("share", parseAsBoolean);

  const { isExpanded, setIsExpanded } = useContext(NotificationBannerHeightContext);
  const containerRef = useSyncNotificationBannerHeight();
  const height = containerRef.current?.offsetHeight || 0;
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setIsExpanded(true);
    return () => setIsExpanded(false);
  }, [setIsExpanded]);

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  const buttonSize = isSmallScreen ? "small" : "medium";
  const buttonFullWidth = isSmallScreen;

  const formOrg = formData.organization;
  const isPaymentReady = formOrg?.isStripeCustomAccountActive;

  const { backToFormTableUrl } = useBackToFormTable();

  const editFormLink = getEditFormLink({
    form: {
      id: formData.id,
      formType,
      status: FormStatus.Active,
    },
    isSmallScreen,
  });

  const shouldDisplayAdditionalStripeInfo = !isPaymentReady;
  const currentOrganizationId = loggedOrganization?.id;

  // When a non logged in user creates a P2P campaign, we store the formId and token in local storage
  // And we use it to display the edit and copy share link buttons
  const { isPeerToPeerCreator, token } = useShouldDisplayFormOwnerBanner({ currentOrganizationId });

  const onCopy = useCallback(() => {
    enqueueSnackbar(t("donationForm", "linkCopied"), {
      vibe: "positive",
      hideClose: true,
      autoHideDuration: AutoHideDuration.fast,
    });
  }, [t]);

  const editLink = getDashboardLink(
    `${DashboardRoutes.FundraiserFormsEdit.replace(":id", formData.id)}&token=${token}`
  );
  const formLink = window.location.href;

  const editCampaignButton = useMemo(
    () => (
      <DashboardButton variant="outlined" vibe="neutral" href={editLink} size={buttonSize} fullWidth={buttonFullWidth}>
        {t("donationForm", "campaignIsLiveDialog.button.editCampaign")}
      </DashboardButton>
    ),
    [editLink, buttonSize, buttonFullWidth, t]
  );

  const copyCampaignLinkButton = useMemo(
    () => (
      <ButtonCopy
        variant="filled"
        vibe="positive"
        onClick={onCopy}
        toCopy={formLink}
        copyText={t("donationForm", "campaignIsLiveDialog.button.copyShareLink")}
        size={buttonSize}
        fullWidth={buttonFullWidth}
      />
    ),
    [buttonSize, buttonFullWidth, t, onCopy, formLink]
  );

  const buttons = useMemo(() => {
    if (isSmallScreen) {
      if (isPaymentReady) {
        return (
          <Grid container columnSpacing={2} sx={{ marginTop: zeffyTheme.spacing(1.5) }}>
            <Grid item xs={6}>
              {isPeerToPeerCreator ? (
                editCampaignButton
              ) : (
                <DashboardButton
                  variant="outlined"
                  href={backToFormTableUrl}
                  size={buttonSize}
                  fullWidth={buttonFullWidth}
                >
                  {t("common", "backToDashboard")}
                </DashboardButton>
              )}
            </Grid>
            <Grid item xs={6}>
              {isPeerToPeerCreator ? (
                copyCampaignLinkButton
              ) : (
                <DashboardButton
                  variant="filled"
                  onClick={() => setIsShareModalOpen(true)}
                  size={buttonSize}
                  fullWidth={buttonFullWidth}
                >
                  {t("common", "shareForm")}
                </DashboardButton>
              )}
            </Grid>
          </Grid>
        );
      } else {
        return (
          <Grid container columnSpacing={2} sx={{ marginTop: zeffyTheme.spacing(1.5) }}>
            <Grid item xs={6}>
              <DashboardButton variant="outlined" href={editFormLink} size={buttonSize} fullWidth={buttonFullWidth}>
                {t("common", "editForm")}
              </DashboardButton>
            </Grid>
            <Grid item xs={6}>
              <DashboardButton variant="filled" href={backToFormTableUrl} size={buttonSize} fullWidth={buttonFullWidth}>
                {t("common", "backToDashboard")}
              </DashboardButton>
            </Grid>
          </Grid>
        );
      }
    } else {
      if (isPaymentReady) {
        return (
          <Stack direction="row" spacing={2}>
            {isPeerToPeerCreator ? (
              editCampaignButton
            ) : (
              <DashboardButton
                variant="outlined"
                href={backToFormTableUrl}
                size={buttonSize}
                fullWidth={buttonFullWidth}
              >
                {t("common", "backToDashboard")}
              </DashboardButton>
            )}
            {isPeerToPeerCreator ? (
              copyCampaignLinkButton
            ) : (
              <DashboardButton
                variant="filled"
                onClick={() => setIsShareModalOpen(true)}
                size={buttonSize}
                fullWidth={buttonFullWidth}
              >
                {t("common", "shareForm")}
              </DashboardButton>
            )}
          </Stack>
        );
      } else {
        return (
          <Stack direction="row" spacing={2}>
            <DashboardButton variant="outlined" href={editFormLink} size={buttonSize} fullWidth={buttonFullWidth}>
              {t("common", "editForm")}
            </DashboardButton>
            <DashboardButton variant="filled" href={backToFormTableUrl} size={buttonSize} fullWidth={buttonFullWidth}>
              {t("common", "backToDashboard")}
            </DashboardButton>
          </Stack>
        );
      }
    }
  }, [
    isSmallScreen,
    isPaymentReady,
    isPeerToPeerCreator,
    editCampaignButton,
    backToFormTableUrl,
    buttonSize,
    buttonFullWidth,
    t,
    copyCampaignLinkButton,
    editFormLink,
    setIsShareModalOpen,
  ]);

  return (
    <ThemeProvider theme={zeffyTheme}>
      <ConnectedShareFormModal
        formId={formData.id}
        formType={formType}
        open={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        source="form_donator_view"
        hideViewFormButton
        sx={{ zIndex: 10000 }}
      />

      <Box
        sx={{
          position: "sticky",
          top: isExpanded ? 0 : `-${height}px`,
          zIndex: 1000,
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: zeffyTheme.palette.surface.neutral.white,
          borderBottomWidth: 1,
          borderBottomStyle: "solid",
          borderColor: zeffyTheme.palette.border.form.quiet,
          paddingY: zeffyTheme.spacing(isSmallScreen ? 1.5 : 2),
          paddingX: zeffyTheme.spacing(isSmallScreen ? 2 : 6),
          marginTop: isExpanded ? 0 : `-${height}px`,
          transition: "margin 0.3s ease-out, top 0.3s ease-out",
        }}
        ref={containerRef}
        data-test="form-owner-banner"
      >
        <Stack direction="row" spacing={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <DashboardIcon>
              <Eye />
            </DashboardIcon>
          </Box>
          <Box>
            <DashboardTypography variant="subtitle1" vibe="text-neutral-moderate">
              {t("common", "adminView")}
            </DashboardTypography>
          </Box>
        </Stack>

        {shouldDisplayAdditionalStripeInfo && (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              display: "flex",
              justifyContent: isSmallScreen ? "flex-start" : "center",
              marginLeft: isSmallScreen ? 0 : 12,
            }}
          >
            <Box
              sx={{
                backgroundColor: theme.palette.surface.brand.quiet,
                paddingBlock: theme.spacing(1.5),
                paddingInline: theme.spacing(2),
                borderRadius: theme.radius(1),
                width: isSmallScreen ? "100%" : "auto",
              }}
            >
              <Stack direction="row" gap={1.5} alignItems="center">
                <Box sx={{ lineHeight: 0 }}>
                  {!isSmallScreen && (
                    <DashboardIcon vibe="text-brand-moderate" size="medium">
                      <Info />
                    </DashboardIcon>
                  )}
                </Box>
                <Box>
                  <DashboardTypography variant="body2" align="left" vibe="text-brand-moderate" component="span">
                    {t("common", "linkBankAccountPrompt1")}{" "}
                    <DashboardLink href={OrganizationRoutes.Bank} sx={{ textDecoration: "underline" }}>
                      {t("common", "linkBankAccountPrompt2")}
                    </DashboardLink>
                    .
                  </DashboardTypography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        )}

        {buttons}

        {/* Expand button */}
        <Box
          ref={buttonRef}
          sx={{
            position: "absolute",
            left: `calc(50% - ${EXPAND_BUTTON_WIDTH / 2}px)`,
            bottom: `-${buttonRef?.current?.offsetHeight || 0}px`,
          }}
          data-test="form-owner-banner-expand-button"
        >
          <DashboardIconButton
            variant="text"
            vibe="neutral"
            size="small"
            sx={{
              zIndex: 200,
              width: `${EXPAND_BUTTON_WIDTH}px`,
              backgroundColor: zeffyTheme.palette.surface.neutral.white,
              border: `1px solid ${zeffyTheme.palette.border.form.quiet}`,
              borderRadius: "0 0 8px 8px !important",
            }}
            slotProps={{
              icon: {
                sx: {
                  transition: "transform 0.3s ease-out",
                  transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)",
                },
              },
            }}
            onClick={handleExpandClick}
          >
            <ChevronUp />
          </DashboardIconButton>
        </Box>
      </Box>
    </ThemeProvider>
  );
};
